import { message } from "antd";
import { useCallback } from "react";

import { useStopwatch } from "core/providers/StopwatchContext";
import { readPersistedSession, getTodayKey } from "../utils/sessionStorage";
import { workSessionEvents } from "./workSessionEvents";
import { buildRequestDescription } from "../utils/buildRequestDescription";
import { workSessionService } from "features/architecture/worksessions/api/work-session.service";
import { WorkSessionPayload } from "../model";
import { getErrorMessage } from "../utils/getErrorMessage";

type Params = {
  collaboratorId?: number;
  projectId?: number;
  phaseId?: number;
  blockId?: number;
  stageId?: number;
  categoryId?: number;
  setProjectAccumulatedSeconds: React.Dispatch<React.SetStateAction<number>>;
};

export function useSessionSave({
  collaboratorId,
  projectId,
  phaseId,
  blockId,
  stageId,
  categoryId,
  setProjectAccumulatedSeconds,
}: Params) {
  const { isRunning, pause, commitSession } = useStopwatch();

  const hasSessionToSave = () => {
    const persisted = readPersistedSession();

    if (!persisted) {
      message.warning("No session data found");
      return false;
    }

    if (persisted.sessionAccumulated <= 0) {
      message.warning("No time to save");
      return false;
    }

    return true;
  };

  const isPreviousDaySession = () => {
    const persisted = readPersistedSession();
    if (!persisted) return false;

    return persisted.sessionDateKey !== getTodayKey();
  };

  const executeSave = useCallback(async () => {
    if (
      !collaboratorId ||
      !projectId ||
      !phaseId ||
      !blockId ||
      !stageId ||
      !categoryId
    )
      return;

    if (isRunning) pause();

    const persisted = readPersistedSession();

    if (!persisted) {
      message.warning("No session data found");
      return;
    }

    const { sessionAccumulated, sessionStartedAt, sessionDateKey } = persisted;

    if (!sessionStartedAt || sessionAccumulated <= 0) {
      message.warning("No time to save");
      return;
    }

    const [year, month, day] = sessionDateKey.split("-").map(Number);
    const original = new Date(sessionStartedAt);

    const startDate = new Date(
      year,
      month - 1,
      day,
      original.getHours(),
      original.getMinutes(),
      original.getSeconds(),
    );

    const startAt = startDate.toISOString();

    const endAt = new Date(
      startDate.getTime() + sessionAccumulated * 1000,
    ).toISOString();

    try {
      const payload: WorkSessionPayload = {
        collaborator_id: collaboratorId,
        hub_project_id: projectId,
        hub_phase_id: phaseId,
        hub_block_id: blockId,
        hub_stage_id: stageId,
        category_id: categoryId,
        start_at: startAt,
        end_at: endAt,
        description: buildRequestDescription({
          type: "create",
          start: startAt,
          end: endAt,
          projectName: "",
        }),
      };

      await workSessionService.createStopwatch(payload);

      workSessionEvents.emit("saved");

      const savedHours = Number((sessionAccumulated / 3600).toFixed(2));
      message.success(`Saved session: ${savedHours}h`);

      commitSession();

      const today = getTodayKey();

      if (sessionDateKey === today) {
        setProjectAccumulatedSeconds((prev) => prev + sessionAccumulated);
      }
    } catch (err) {
      console.error(err);
      message.error(getErrorMessage(err, "Failed to save session"));
    }
  }, [
    collaboratorId,
    projectId,
    phaseId,
    blockId,
    stageId,
    categoryId,
    isRunning,
    pause,
    commitSession,
    setProjectAccumulatedSeconds,
  ]);

  return {
    executeSave,
    hasSessionToSave,
    isPreviousDaySession,
  };
}

